// src/features/FieldService/MobileWorkOrders.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Chip,
  Stack,
  Button,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Badge
} from '@mui/material';
import {
  PlayArrow,
  Pause,
  CheckCircle,
  CameraAlt,
  Assignment,
  Schedule,
  Warning
} from '@mui/icons-material';
import { useI18n } from '../../i18n/i18n';
import { WorkOrderService } from '../../services/workOrderService';
import PhotoCapture from '../../components/PhotoCapture';

const MobileWorkOrders = ({ user }) => {
  const { t } = useI18n();
  const [workOrderService] = useState(new WorkOrderService());
  const [workOrders, setWorkOrders] = useState([]);
  const [activeOrder, setActiveOrder] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [timer, setTimer] = useState(0);
  const [timerInterval, setTimerInterval] = useState(null);
  const [photoDialog, setPhotoDialog] = useState({ open: false, step: null });
  const [completionDialog, setCompletionDialog] = useState(false);

  useEffect(() => {
    loadWorkOrders();
  }, []);

  useEffect(() => {
    return () => {
      if (timerInterval) clearInterval(timerInterval);
    };
  }, [timerInterval]);

  const loadWorkOrders = () => {
    const technicianOrders = workOrderService.getWorkOrdersForTechnician(user?.username || 'tech_001');
    setWorkOrders(technicianOrders);
  };

  const startWorkOrder = (order) => {
    setActiveOrder(order);
    setActiveStep(0);
    setTimer(0);
    
    // Запуск таймера
    const interval = setInterval(() => {
      setTimer(prev => prev + 1);
    }, 1000);
    
    setTimerInterval(interval);
    
    // Оновлення статусу
    workOrderService.updateWorkOrderStatus(order.id, 'in_progress');
    loadWorkOrders();
  };

  const pauseWorkOrder = () => {
    if (timerInterval) {
      clearInterval(timerInterval);
      setTimerInterval(null);
    }
  };

  const resumeWorkOrder = () => {
    const interval = setInterval(() => {
      setTimer(prev => prev + 1);
    }, 1000);
    setTimerInterval(interval);
  };

  const completeStep = async (stepIndex) => {
    if (!activeOrder) return;

    const updatedOrder = { ...activeOrder };
    if (!updatedOrder.completedSteps) updatedOrder.completedSteps = [];
    updatedOrder.completedSteps.push(stepIndex);

    await workOrderService.updateWorkOrderStatus(activeOrder.id, 'in_progress', {
      completedSteps: updatedOrder.completedSteps
    });

    setActiveOrder(updatedOrder);
    
    // Перехід до наступного кроку
    if (stepIndex < activeOrder.steps.length - 1) {
      setActiveStep(stepIndex + 1);
    } else {
      setCompletionDialog(true);
    }
  };

  const completeWorkOrder = async () => {
    if (!activeOrder) return;

    await workOrderService.updateWorkOrderStatus(activeOrder.id, 'completed', {
      actualTime: Math.round(timer / 60), // Конвертуємо в хвилини
      completedAt: new Date().toISOString()
    });

    if (timerInterval) {
      clearInterval(timerInterval);
      setTimerInterval(null);
    }

    setActiveOrder(null);
    setCompletionDialog(false);
    loadWorkOrders();
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'default';
    }
  };

  return (
    <Box sx={{ p: 2, maxWidth: 800, mx: 'auto' }}>
      <Typography variant="h4" fontWeight="bold" gutterBottom>
        {t('work_orders_title')}
      </Typography>

      {/* Активний робочий наряд */}
      {activeOrder && (
        <Card sx={{ mb: 3, bgcolor: 'primary.light', color: 'white' }}>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="h6">{activeOrder.title}</Typography>
                <Typography variant="body2">
                  {t('machine')}: {activeOrder.machineName}
                </Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4">{formatTime(timer)}</Typography>
                <Typography variant="caption">
                  {t('elapsed_time')}
                </Typography>
              </Box>
              <Stack direction="row" spacing={1}>
                {timerInterval ? (
                  <IconButton onClick={pauseWorkOrder} sx={{ color: 'white' }}>
                    <Pause />
                  </IconButton>
                ) : (
                  <IconButton onClick={resumeWorkOrder} sx={{ color: 'white' }}>
                    <PlayArrow />
                  </IconButton>
                )}
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Кроки виконання */}
      {activeOrder && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {t('work_steps')}
            </Typography>
            <Stepper activeStep={activeStep} orientation="vertical">
              {activeOrder.steps.map((step, index) => (
                <Step key={step.step} completed={activeOrder.completedSteps?.includes(index)}>
                  <StepLabel>
                    {step.action}
                  </StepLabel>
                  <StepContent>
                    <Typography>{step.description}</Typography>
                    <Box sx={{ mt: 2 }}>
                      <Stack direction="row" spacing={1}>
                        <Button
                          variant="contained"
                          onClick={() => completeStep(index)}
                          disabled={activeOrder.completedSteps?.includes(index)}
                        >
                          {t('complete_step')}
                        </Button>
                        <Button
                          variant="outlined"
                          startIcon={<CameraAlt />}
                          onClick={() => setPhotoDialog({ open: true, step: index })}
                        >
                          {t('add_photo')}
                        </Button>
                      </Stack>
                    </Box>
                  </StepContent>
                </Step>
              ))}
            </Stepper>
          </CardContent>
        </Card>
      )}

      {/* Список доступних нарядів */}
      {!activeOrder && (
        <Stack spacing={2}>
          {workOrders.filter(wo => wo.status === 'assigned').map(order => (
            <Card key={order.id}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      {order.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {order.machineName} • {t('estimated_time')}: {order.estimatedTime}min
                    </Typography>
                    <Typography variant="body2">
                      {order.description}
                    </Typography>
                  </Box>
                  <Stack spacing={1} alignItems="flex-end">
                    <Chip
                      label={t(`priority_${order.priority}`)}
                      color={getPriorityColor(order.priority)}
                      size="small"
                    />
                    <Button
                      variant="contained"
                      startIcon={<PlayArrow />}
                      onClick={() => startWorkOrder(order)}
                    >
                      {t('start_work')}
                    </Button>
                  </Stack>
                </Stack>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      {/* Діалог додавання фото */}
      <Dialog 
        open={photoDialog.open} 
        onClose={() => setPhotoDialog({ open: false, step: null })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {t('add_step_photo')} - {t('step')} {photoDialog.step + 1}
        </DialogTitle>
        <DialogContent>
          <PhotoCapture
            onPhotosChange={(photos) => {
              if (activeOrder && photos.length > 0) {
                workOrderService.addPhotoToWorkOrder(activeOrder.id, photos[0]);
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPhotoDialog({ open: false, step: null })}>
            {t('close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Діалог завершення роботи */}
      <Dialog open={completionDialog} onClose={() => setCompletionDialog(false)}>
        <DialogTitle>{t('complete_work_order')}</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            {t('confirm_completion')} "{activeOrder?.title}"?
          </Typography>
          <TextField
            label={t('work_notes')}
            multiline
            rows={3}
            fullWidth
            sx={{ mt: 2 }}
            placeholder={t('notes_placeholder')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCompletionDialog(false)}>
            {t('cancel')}
          </Button>
          <Button onClick={completeWorkOrder} variant="contained" color="success">
            {t('complete_and_submit')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Статистика */}
      {!activeOrder && workOrders.length > 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {t('your_stats')}
            </Typography>
            <Stack direction="row" spacing={3} justifyContent="space-around">
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="primary.main">
                  {workOrderService.getTechnicianStats(user?.username || 'tech_001').completed}
                </Typography>
                <Typography variant="body2">{t('completed')}</Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="warning.main">
                  {workOrders.filter(wo => wo.status === 'assigned').length}
                </Typography>
                <Typography variant="body2">{t('assigned')}</Typography>
              </Box>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" color="error.main">
                  {workOrderService.getTechnicianStats(user?.username || 'tech_001').overdue}
                </Typography>
                <Typography variant="body2">{t('overdue')}</Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default MobileWorkOrders;